import { type ReactElement, type ReactNode, useState } from "react";

import { Notice } from "@/components/Notice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { type ActionOutcome, LOAD_FAILED } from "@/lib/api";

/**
 * A red button for a step that cannot be undone, asked once more in a dialog. The answer of
 * the server stands under the button, in red when it refused.
 */
export function ConfirmButton({
  label,
  question,
  consequence,
  confirmLabel,
  disabled = false,
  run,
  onDone,
}: {
  readonly label: string;
  /** The dialog's title, a question: "Aktion endgültig beenden?" */
  readonly question: string;
  /** What happens, in one or two sentences. */
  readonly consequence: ReactNode;
  readonly confirmLabel: string;
  readonly disabled?: boolean;
  readonly run: () => Promise<ActionOutcome>;
  readonly onDone: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);

  const handleConfirm = (): void => {
    setBusy(true);
    setOutcome(null);
    run()
      .then((result) => {
        setOutcome(result);
        if (result.ok) {
          onDone();
        }
      })
      .catch((error: unknown) => {
        console.error("[ConfirmButton] step failed", label, error);
        setOutcome({ ok: false, message: LOAD_FAILED });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="grid justify-items-start gap-3">
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" disabled={disabled || busy}>
            {busy ? "Wird ausgeführt …" : label}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{question}</AlertDialogTitle>
            <AlertDialogDescription>{consequence}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleConfirm}>
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {outcome !== null && !outcome.ok ? (
        <Notice tone="error" className="whitespace-pre-line">
          {outcome.message}
        </Notice>
      ) : null}
    </div>
  );
}
